import { Breakpoint } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { PropsWithChildren, useState } from 'react';

type Props = {
  opendialog: boolean;
  dialogTitle: string;
  handleClose?: () => void;
  btnCloseText?: string;
  btnCloseTestIdPrefix?: string;
  btnSuccessText?: string;
  btnSuccessTestIdPrefix?: string;
  buttonColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  successButtonDisable?: boolean;
  handleSuccess: () => Promise<void | true>;
  dialogTestIdSuffix?: string;
  closeButtonDisable?: boolean;
  maxWidth?: false | Breakpoint;
  disableCloseOnBackdropOrEscape?: boolean;
};

const CustomDialog = ({
  opendialog,
  dialogTitle,
  handleClose: handleDialogClose,
  btnCloseText = 'Cancel',
  btnCloseTestIdPrefix = 'cancel',
  btnSuccessText = 'Save',
  btnSuccessTestIdPrefix = 'save',
  buttonColor = 'primary',
  successButtonDisable: saveDisable,
  closeButtonDisable: cancelDisable = false,
  handleSuccess: handleSaveDialog,
  children,
  dialogTestIdSuffix,
  maxWidth,
  disableCloseOnBackdropOrEscape = false,
}: PropsWithChildren<Props>) => {
  const [areButtonsDisabled, setButtonsDisabled] = useState(false);

  return (
    <Dialog
      open={opendialog}
      onClose={async (_event, reason) => {
        if (
          disableCloseOnBackdropOrEscape &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        ) {
          return;
        }
        if (areButtonsDisabled) return;
        if (handleDialogClose) handleDialogClose();
      }}
      fullWidth
      maxWidth={maxWidth ?? 'xs'}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ '& > :last-child': { marginBottom: 0 } }}>{children}</DialogContent>
      <DialogActions>
        {handleDialogClose && (
          <Button
            onClick={handleDialogClose}
            color={buttonColor}
            data-testid={`${btnCloseTestIdPrefix}-${dialogTestIdSuffix}`}
            disabled={cancelDisable || areButtonsDisabled}
          >
            {btnCloseText}
          </Button>
        )}
        <Button
          onClick={async () => {
            setButtonsDisabled(true);
            const unmounted = await handleSaveDialog();
            if (unmounted) return;
            setButtonsDisabled(false);
          }}
          disabled={saveDisable || areButtonsDisabled}
          color={buttonColor}
          data-testid={`${btnSuccessTestIdPrefix}-${dialogTestIdSuffix}`}
        >
          {btnSuccessText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CustomDialog;
